import { Injectable, inject } from '@angular/core';
import { ClimateApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/climate/climate.api';
import { StorageService } from '../front/storage.service';
import { DataStore } from './data-store.service';
import { nearestClimateVariant } from '../../utils/climate-variant.util';

/**
 * Le climat ne change qu'avec l'adresse, et toute écriture sur l'adresse
 * vide le cache (voir `DataStore`) : il peut être resservi longtemps.
 */
const CLIMATE_TTL_MS = 10 * 60_000;

/**
 * Le climat de Köppen du jardin de l'app (l'adresse enregistrée), lu une fois
 * pour tous les écrans : l'ajout de plante, le calendrier, les fiches d'une
 * culture ou d'une semence.
 */
@Injectable({ providedIn: 'root' })
export class GardenClimateStore {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme `GardenTodoStore`.
  private readonly climateApi = new ClimateApi();
  private readonly data = inject(DataStore);
  private readonly storage = inject(StorageService);

  /**
   * Le climat du jardin ; `null` sans adresse, sans climat connu ou en panne :
   * le climat n'empêche jamais un écran de s'ouvrir.
   */
  async climate(): Promise<string | null> {
    const addressUid = this.storage.getItem('address-uid');
    if (!addressUid) return null;
    try {
      const climate = await this.data.get(
        `garden/climate:${addressUid}`,
        () => this.climateApi.getMyClimate(addressUid),
        { ttlMs: CLIMATE_TTL_MS },
      );
      return climate?.climateType || null;
    } catch (e) {
      console.warn('GardenClimateStore: climate unavailable', e);
      return null;
    }
  }

  /**
   * La variante d'une culture pour le climat du jardin, sinon du plus proche
   * (voir `nearestClimateVariant`) ; les variantes et le climat se lisent en
   * même temps.
   */
  async variantFor<T extends { climateType?: string | null }>(variants: Promise<T[] | null | undefined>): Promise<T | undefined> {
    const [list, climate] = await Promise.all([variants, this.climate()]);
    return nearestClimateVariant(list, climate);
  }
}
